import React, { useMemo, useState } from "react";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Grid,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import LabelPreview from "./LabelPreview";
import { usePlatform } from "../platform/store";
import { generateLabels } from "../platform/labelGenerator";
import {
  InlineActions,
  PageRoot,
  SecondaryAction,
  SectionCard,
} from "./ui/PlatformPrimitives";

// Severity readiness pills — translucent rgba tint over glass (14% bg /
// 22% bg+ring). Severity contrast text (#92400E / #991B1B / #2D6A4F) is
// allow-listed per Liquid Glass spec.
const Readiness = ({ readiness }) => {
  const { eu, us, stateFindings } = readiness;
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
      <Chip
        label={`EU ${eu.ready ? "✓" : `· ${eu.missing}`}`}
        size="small"
        color={eu.ready ? "success" : "error"}
        sx={{ borderRadius: 999 }}
      />
      <Chip
        label={`US ${us.ready ? "✓" : `· ${us.missing}`}`}
        size="small"
        color={us.ready ? "success" : "error"}
        sx={{ borderRadius: 999 }}
      />
      {stateFindings > 0 && (
        <Chip
          label={`${stateFindings} state`}
          size="small"
          color="warning"
          sx={{ borderRadius: 999 }}
        />
      )}
    </Stack>
  );
};

const LabelingPage = () => {
  const { products, regulatoryRules } = usePlatform();
  const [searchParams, setSearchParams] = useSearchParams();
  const paramProductId = searchParams.get("product");
  const [selectedProductId, setSelectedProductId] = useState(
    paramProductId || products[0]?.id || ""
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [marketFilter, setMarketFilter] = useState("");
  const [readinessFilter, setReadinessFilter] = useState("all");

  // Compute readiness once per product. Cheap because labelGenerator is pure.
  const productReadiness = useMemo(() => {
    const map = new Map();
    products.forEach((product) => {
      if (!product?.compliance?.activeFormula) {
        map.set(product.id, {
          eu: { ready: false, missing: 0 },
          us: { ready: false, missing: 0 },
          stateFindings: 0,
          hasFormula: false,
        });
        return;
      }
      try {
        const labels = generateLabels({
          product,
          compliance: product.compliance,
          regulatoryRules,
        });
        map.set(product.id, {
          eu: { ready: labels.summary.euReady, missing: labels.summary.euMissing },
          us: { ready: labels.summary.usReady, missing: labels.summary.usMissing },
          stateFindings: labels.summary.stateFindings || 0,
          hasFormula: true,
        });
      } catch {
        map.set(product.id, {
          eu: { ready: false, missing: 0 },
          us: { ready: false, missing: 0 },
          stateFindings: 0,
          hasFormula: true,
        });
      }
    });
    return map;
  }, [products, regulatoryRules]);

  const selectProduct = (id) => {
    setSelectedProductId(id);
    setSearchParams({ product: id }, { replace: true });
  };

  const filteredProducts = useMemo(
    () =>
      products.filter((product) => {
        const matchesSearch =
          !searchQuery ||
          product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          product.brand.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesMarket = !marketFilter || product.markets.includes(marketFilter);
        const r = productReadiness.get(product.id);
        const matchesReadiness =
          readinessFilter === "all" ||
          (readinessFilter === "ready" && r?.eu.ready && r?.us.ready && r?.stateFindings === 0) ||
          (readinessFilter === "not_ready" && (!r?.eu.ready || !r?.us.ready)) ||
          (readinessFilter === "state_warnings" && r?.stateFindings > 0);
        return matchesSearch && matchesMarket && matchesReadiness;
      }),
    [marketFilter, products, productReadiness, readinessFilter, searchQuery]
  );

  const selectedProduct =
    products.find((product) => product.id === selectedProductId) || filteredProducts[0] || null;

  const counts = useMemo(() => {
    let ready = 0;
    let notReady = 0;
    let stateWarn = 0;
    productReadiness.forEach((r) => {
      if (r.eu.ready && r.us.ready && r.stateFindings === 0) ready += 1;
      if (!r.eu.ready || !r.us.ready) notReady += 1;
      if (r.stateFindings > 0) stateWarn += 1;
    });
    return { ready, notReady, stateWarn, all: productReadiness.size };
  }, [productReadiness]);

  return (
    <PageRoot>
      <SectionCard
        title="Labeling"
        action={
          <InlineActions>
            <SecondaryAction component={RouterLink} to="/perfume-dashboard">
              Back to Formula Library
            </SecondaryAction>
          </InlineActions>
        }
      >
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }} flexWrap="wrap" useFlexGap>
          <TextField
            size="small"
            placeholder="Search products…"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon sx={{ fontSize: 16, color: "text.disabled" }} />
                  </InputAdornment>
                ),
              },
            }}
            sx={{ minWidth: 200, flex: 1 }}
          />
          <TextField
            size="small"
            select
            value={marketFilter}
            onChange={(event) => setMarketFilter(event.target.value)}
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="">All markets</MenuItem>
            <MenuItem value="EU">EU</MenuItem>
            <MenuItem value="US">US</MenuItem>
          </TextField>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {[
              { key: "all", label: `All (${counts.all})` },
              { key: "not_ready", label: `Not ready (${counts.notReady})` },
              { key: "state_warnings", label: `State (${counts.stateWarn})` },
              { key: "ready", label: `Ready (${counts.ready})` },
            ].map((opt) => {
              const active = readinessFilter === opt.key;
              return (
                <Chip
                  key={opt.key}
                  label={opt.label}
                  size="small"
                  onClick={() => setReadinessFilter(opt.key)}
                  sx={{
                    cursor: "pointer",
                    borderRadius: 999,
                    // Active: solid navy brand-mark fill (allow-listed
                    // #1B2A4A); idle: theme glass-inset pill (inherits
                    // backdrop-filter from MuiChip).
                    bgcolor: active ? "#1B2A4A" : undefined,
                    color: active ? "common.white" : undefined,
                    border: active ? "1px solid transparent" : undefined,
                    fontWeight: 600,
                  }}
                />
              );
            })}
          </Stack>
        </Stack>
      </SectionCard>

      {filteredProducts.length === 0 ? (
        <Box sx={{ py: 5, textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            {products.length === 0
              ? "No products to label yet. Create one in the Compliance Checker."
              : "No products match this filter."}
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          <Grid
            size={{ xs: 12, md: 4 }}
            sx={{
              // Sticky lives on the Grid item itself — alignSelf:flex-start
              // opts out of Grid v2's flex-stretch so the item height equals
              // its own content height, leaving the surrounding container
              // taller than the item (that's what sticky needs to be able to
              // pin while the right pane keeps scrolling).
              position: { xs: "static", md: "sticky" },
              top: { md: 16 },
              alignSelf: "flex-start",
            }}
          >
            <Stack
              spacing={1.5}
              sx={{
                // Internal scroll only on long catalogues, so the list doesn't
                // overflow the viewport. Kept on the inner wrapper so it
                // doesn't fight position:sticky on the outer Grid item.
                // Leave 96px for app header + page padding — `100vh - 32`
                // overshoots and pushes the bottom of the list below the
                // visible viewport on standard desktops. No explicit bg so the
                // page gradient shows behind each (auto-glass) product card.
                maxHeight: { md: "calc(100vh - 96px)" },
                overflowY: { md: "auto" },
                overflowX: "hidden",
                pr: { md: 0.5 },
                minWidth: 0,
                borderRadius: 2,
                background: "transparent",
              }}
            >
              {filteredProducts.map((product) => {
                const isSelected = selectedProduct?.id === product.id;
                const r = productReadiness.get(product.id);
                return (
                  <Card
                    key={product.id}
                    sx={{
                      // Selected card lifts a tier — translucent navy focus
                      // ring (inset shadow) + slightly more opaque glass; idle
                      // card stays at the theme default.
                      background: isSelected ? "rgba(255,255,255,0.55)" : undefined,
                      boxShadow: isSelected
                        ? "inset 0 0 0 1.5px rgba(27,42,74,0.40)"
                        : undefined,
                    }}
                  >
                    <CardActionArea onClick={() => selectProduct(product.id)} sx={{ p: 0, borderRadius: "inherit" }}>
                      <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 }, position: "relative", zIndex: 1 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" color="text.primary" sx={{ fontWeight: 600 }}>
                              {product.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {product.brand} · {product.markets.join(" · ")}
                            </Typography>
                          </Box>
                        </Stack>
                        {r && r.hasFormula && (
                          <Box sx={{ mt: 1 }}>
                            <Readiness readiness={r} />
                          </Box>
                        )}
                        {r && !r.hasFormula && (
                          <Typography variant="caption" color="text.disabled" sx={{ mt: 1, display: "block", fontStyle: "italic" }}>
                            No saved formula
                          </Typography>
                        )}
                      </CardContent>
                    </CardActionArea>
                  </Card>
                );
              })}
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, md: 8 }} sx={{ minWidth: 0 }}>
            {selectedProduct?.compliance?.activeFormula ? (
              <LabelPreview
                product={selectedProduct}
                compliance={selectedProduct.compliance}
                regulatoryRules={regulatoryRules}
              />
            ) : (
              <Box sx={{ py: 5, textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  Select a product with a saved formula to view label drafts.
                </Typography>
              </Box>
            )}
          </Grid>
        </Grid>
      )}
    </PageRoot>
  );
};

export default LabelingPage;
